import React from "react";
import ServicePage from "./ServicePage";

export default function ApiBackend() {
  return (
    <ServicePage
      slug="api-backend"
      eyebrow="Service · 03"
      title="API &"
      titleAccent="Backend."
      intro="Node.js, Express, Laravel and PHP backends with MongoDB, MySQL and Elasticsearch. Clean APIs, sane data models, fast queries — and code your future on-call self can actually read."
      capabilities={[
        {
          title: "API design & build",
          desc: "REST or GraphQL, depending on the call site. Versioned, documented, with typed clients generated where it pays off.",
          points: [
            "OpenAPI / GraphQL schema as a contract",
            "Authentication, authorization, rate limiting from day one",
            "Generated TypeScript clients for the frontend team",
          ],
        },
        {
          title: "Data modelling & search",
          desc: "Relational or document, indexed for the access patterns you actually have. Elasticsearch for product search, log search and analytics.",
          points: [
            "Schema design with read/write patterns in mind",
            "Index strategy (primary + secondary, partial, covering)",
            "Elasticsearch mapping, analyzers, synonyms",
          ],
        },
        {
          title: "Background jobs & queues",
          desc: "BullMQ / Redis pipelines for long-running work — webhooks, bulk imports, order processing, AI pipelines. Idempotent, retried, observable.",
          points: [
            "Idempotency keys + dedupe windows",
            "Dead-letter queues with replay tooling",
            "Backpressure when downstream slows down",
          ],
        },
        {
          title: "Integrations & ETL",
          desc: "Third-party APIs (payment, ERP, CRM, marketing), webhooks, data syncs. Reliable plumbing instead of fragile cron scripts.",
          points: [
            "Webhook receivers with replay + audit trail",
            "Outbound retries with exponential backoff",
            "Data reconciliation jobs you can trust",
          ],
        },
      ]}
      process={[
        { title: "Model the domain", desc: "Walk the user flows, name the entities, draft the API surface before any code." },
        { title: "Build the spine", desc: "Auth, persistence, observability, deployment pipeline — the boring scaffolding done well." },
        { title: "Ship the slices", desc: "Each endpoint behind a feature flag, demoed end-to-end with the frontend." },
        { title: "Harden", desc: "Load test, attack surface review, runbooks for the on-call team." },
      ]}
      outcomes={[
        { kpi: "Reliability", title: "99.9%+ uptime targets", desc: "Designed for graceful degradation when a dependency wobbles." },
        { kpi: "Performance", title: "Sub-100ms p50", desc: "Honest latency budgets, with the slow endpoints called out and fixed." },
        { kpi: "Operability", title: "Runbooks included", desc: "Every alert has a documented response. No tribal knowledge." },
      ]}
      techHighlights={[
        "Node.js / Express",
        "Laravel / PHP",
        "MongoDB",
        "MySQL / PostgreSQL",
        "Elasticsearch",
        "Redis / BullMQ",
        "Docker",
        "REST + GraphQL",
        "OpenAPI / Zod",
      ]}
      faqs={[
        {
          q: "Node.js or Laravel?",
          a: "Whichever fits your team. Laravel is faster for CRUD-heavy back-offices and admin tools — the ecosystem is excellent. Node fits better when there's heavy realtime, streaming, or shared TypeScript types with a React frontend. I write both, and the choice is about your team's center of gravity, not fashion.",
        },
        {
          q: "MongoDB or MySQL/Postgres?",
          a: "Relational is the default. Pick MongoDB when you have legitimately schema-less data (event payloads, content models with rich variations) or per-tenant flexibility needs. Hybrid (relational + a small Mongo collection) is a sane middle ground for many e-commerce apps.",
        },
        {
          q: "Where do you deploy?",
          a: "I'm cloud-agnostic. AWS / GCP / Vercel / Railway / Fly / a bare Linux VPS with Docker — whatever the team can operate. Preference is for boring, well-supported choices.",
        },
        {
          q: "Do you handle infra and DevOps?",
          a: "Up to a reasonable line — containerization, CI/CD pipelines, deploy scripts, basic monitoring. Below that line (full Kubernetes platform engineering, multi-region) you want a dedicated platform team.",
        },
      ]}
    />
  );
}
